/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then, When } from "react-if";
import styled from "styled-components";
import { v4 } from "uuid";
import { ExtendedTooltip, Tag, Text, theme } from "@nordcloud/gnui";
import { CloudAccountRegionStatus } from "~/generated/graphql";
import { useDisclosure } from "~/hooks";
import { useCloudAccount } from "../../CloudAccountProvider";

export function DisplayRegions() {
  const { cloudAccount } = useCloudAccount();

  const { open, close, isOpen } = useDisclosure();

  const regionStatusList =
    cloudAccount?.regionsStatuses?.filter(
      (region) => !cloudAccount?.regions.includes(region?.status ?? "")
    ) ?? [];

  const regions = cloudAccount?.regions
    .map((region) => {
      if (regionStatusList.map((item) => item?.region).includes(region)) {
        return regionStatusList.find(
          (regionStatus) => regionStatus?.region === region
        );
      } else {
        return {
          region,
          details: undefined,
          status: undefined,
        };
      }
    })
    .map((regionStatus) => {
      return {
        id: v4(),
        regionStatus,
      };
    });

  return (
    <Wrap>
      <CollapseWrapper isExpanded={isOpen}>
        {regions?.map((item) => {
          return (
            <RegionStatusTag key={item?.id} regionStatus={item.regionStatus} />
          );
        })}
      </CollapseWrapper>
      <When condition={regions && regions?.length > 6}>
        <If condition={isOpen}>
          <Then>
            <Btn onClick={close}>show less...</Btn>
          </Then>
          <Else>
            <Btn onClick={open}>show more...</Btn>
          </Else>
        </If>
      </When>
    </Wrap>
  );
}

type Props = {
  regionStatus?: {
    region: string;
    details?: string | null;
    status?: string;
  } | null;
};

function RegionStatusTag({ regionStatus }: Props) {
  if (regionStatus?.status === CloudAccountRegionStatus.AccessError) {
    return (
      <ExtendedTooltip
        status="danger"
        position="start"
        caption={
          <>
            <Text
              size="sm"
              color={theme.colors.white}
              tag="div"
              mb={theme.spacing.spacing01}
            >
              Inaccessible
            </Text>
            <Text color={theme.colors.white} size="xs" tag="div">
              {regionStatus.details}
            </Text>
          </>
        }
      >
        <Tag text={regionStatus.region} color="danger" />
      </ExtendedTooltip>
    );
  }
  if (regionStatus?.status === CloudAccountRegionStatus.AccessOk) {
    return (
      <ExtendedTooltip status="success" position="start" caption="Accessible">
        <Tag text={regionStatus.region} color="success" />
      </ExtendedTooltip>
    );
  }
  return (
    <ExtendedTooltip position="start" caption="Region Status is not available">
      <Tag text={regionStatus?.region} />
    </ExtendedTooltip>
  );
}

const Wrap = styled.div`
  position: relative;
`;

const Btn = styled.div`
  position: absolute;
  background: none;
  bottom: -${theme.spacing.spacing04};
  right: 0;
  border: none;
  color: ${theme.color.interactive.link};
  z-index: ${theme.zindex.default};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
`;

const CollapseWrapper = styled.div<{ isExpanded: boolean }>`
  margin-top: ${theme.spacing.spacing01};
  height: ${({ isExpanded }) => (isExpanded ? "auto" : "2rem")};
  overflow: hidden;
  z-index: ${theme.zindex.zero};
`;
